import { useRegion } from 'common/hooks/region';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyReturnAvailableResponse, Filter } from 'services/clients/perDiem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Button, Card, CardContent } from '@mui/material';
import { formatCurrencyNumber } from 'utils/common';
import { fetchShippingLines, ShippingLineQueryKeys } from 'services/clients/shippingLine';
import mediaQueries from 'mediaqueries';
import { CopyTextButton } from 'components/CopyTextButton';
import { Permission, useHasPermission } from 'utils/permissions';
import { StatBlock } from './StatBlock';

interface Props {
  resultData?: EmptyReturnAvailableResponse;
  className?: string;
  setOpenModal: (modal: { modal: 'download' | 'upgrade' }) => void;
  reportFilter: Filter;
}

export const StatCard: React.FC<Props> = (props) => {
  const { resultData, className, setOpenModal, reportFilter } = props;
  const region = useRegion();

  const smallScreen = useMediaQuery(`(max-width:${mediaQueries.sm})`);

  const opportunityToFightPercentage = React.useMemo(() => {
    if (resultData?.opportunityToFight || resultData?.opportunityToFight === 0) {
      return `${Math.floor(resultData.opportunityToFight * 100)}%`;
    }
    return undefined;
  }, [resultData]);

  const shippingLines = useQuery({
    queryKey: [ShippingLineQueryKeys.ShippingLines, region.code],
    queryFn: ({ signal }) => fetchShippingLines({ signal, portCollectionCodes: [region.code] }),
    onError: (e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    },
  });

  const shippingLineEmail = React.useMemo(() => {
    if (!shippingLines.data) {
      return undefined;
    }
    const shippingLine = shippingLines.data.find((line) => line.key === reportFilter.shippingLine);
    return shippingLine?.emails ? shippingLine?.emails[0].emailDisplay : undefined;
  }, [shippingLines.data, reportFilter.shippingLine]);

  const hasPermissionToSend = useHasPermission([Permission.PerDiemSmartPerDiemDownload]);
  const disableSend = resultData?.opportunityToFight === 0 || !hasPermissionToSend;

  return (
    <Card className={`shadow-md bg-blue-50 ${className}`} style={{ borderRadius: 16 }}>
      <CardContent className="flex items-center justify-around w-full h-full">
        <div className="flex justify-around w-full">
          <StatBlock
            tooltipTitle="Total days in report"
            title="# of days"
            value={resultData?.dayCount}
          />
          <StatBlock
            tooltipTitle="% of days that can be disputed"
            title="Opportunity"
            value={opportunityToFightPercentage}
          />
          <StatBlock
            tooltipTitle="Estimated savings are calculated by multiplying the numbers of days where you can fight be the Per Diem daily amount form the SSL"
            title="Est. savings"
            value={formatCurrencyNumber(resultData?.estimatedSavings)}
          />
        </div>
        <div className="text-center">
          <Button
            variant="contained"
            color="secondary"
            disabled={disableSend}
            onClick={() => setOpenModal({ modal: 'download' })}
            className="rounded-lg mb-6"
            sx={{
              minHeight: smallScreen ? 33 : 46,
              minWidth: 151,
              width: smallScreen ? 151 : 224,
              fontSize: smallScreen ? 14 : 16,
              letterSpacing: 0,
            }}
          >
            Send email report
          </Button>
          {shippingLineEmail && (
            <div className="flex gap-2 justify-center items-center">
              <p className="text-base text-blue-900 md:text-sm sm:text-xs">{shippingLineEmail}</p>
              <CopyTextButton value={shippingLineEmail} label={shippingLineEmail} variant="icon" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
StatCard.displayName = 'PerDiemArchive.SmartPerDiem.StatCard';
